import { checkedValidator, emailValidator, emptyValidator } from 'services/validators-functions'
import storageService from 'services/storage'
import { onConsentReady } from 'services/cookiebot'
import { promiseWrapper } from 'utils/promise'
import { checkCheckbox, checkEmail, clearValidationError } from 'services/input-validators'
import { toggleSpinner } from 'components/button-with-spinner'
import { pushDataLayer } from 'utils/data-layer-push'
import { getCurrentLanguage } from 'utils/current-language'
import type { Language } from 'utils/languages'

declare let USER_BASE_URL_IK: string

interface FormPayload {
  email: string
  language: Language
}

const $modal = document.querySelector<HTMLDialogElement>('[data-js-selector="promo-popup"]')
const $modalStep1 = document.querySelector<HTMLDivElement>('[data-js-selector="promo-popup__step1"]')
const $modalStep2 = document.querySelector<HTMLDivElement>('[data-js-selector="promo-popup__step2"]')
const $form = document.querySelector<HTMLFormElement>('[data-js-selector="promo-popup-form"]')
const $submitButton = document.querySelector<HTMLButtonElement>('[data-js-selector="promo-popup-submit-button"]')
const $emailInput = document.querySelector<HTMLInputElement>('[name="promo-popup-email"]')

const $emailErrorWrapper = $form?.querySelector<HTMLDivElement>('[data-js-selector="promo-popup-email-error-message"]')
const $checkboxErrorWrapper = document.querySelector<HTMLDivElement>(
  '[data-js-selector="promo-popup-checkbox-error-message"]',
)
const $serverErrorWrapper = document.querySelector<HTMLDivElement>(
  '[data-js-selector="promo-popup-server-error-message"]',
)
const $accountExistsErrorWrapper = document.querySelector<HTMLDivElement>(
  '[data-js-selector="promo-popup-account-exists-message"]',
)

const $checkboxLabel = document.querySelector<HTMLInputElement>('[name="promo-popup-rodo-checkbox"]')
const $checkboxInput = document.querySelector<HTMLInputElement>('[data-js-selector="promo-popup-checkbox-input"]')
const $checkboxHolder = document.querySelector<HTMLDivElement>('[data-js-selector="promo-popup-checkbox-holder"]')
const $checkmarkWrapper = document.querySelector<HTMLDivElement>('[data-js-selector="promo-popup-checkmark-wrapper"]')
const $checkmarkIcon = document.querySelector<HTMLDivElement>('[data-js-selector="promo-popup-checkmark-icon"]')

const $modalCloseIcon = $modal?.querySelector<HTMLDivElement>('[data-js-selector="promo-popup-close-icon"]')
const $modalCloseButton = $modal?.querySelector<HTMLDivElement>('[data-js-selector="promo-popup-close-button"]')

const closeModal = (): void => {
  storageService.setItem('promo_popup_closed', 'true')
  $modal.close()
}

const delayedShowModal = (): void => {
  setTimeout(() => {
    $modal.showModal()
  }, 10 * 1000)
}

const showModal = (): void => {
  if (storageService.getItem('promo_popup_closed')) return

  onConsentReady(delayedShowModal)
}

const toggleCheckbox = (): void => {
  if ($checkmarkWrapper) $checkmarkWrapper.classList.toggle('is-checked')
  if ($checkmarkIcon) $checkmarkIcon.toggleAttribute('hidden')
}

const showCheckbox = (): void => {
  const value = $emailInput.value
  if (!emptyValidator(value) && !emailValidator(value)) {
    $checkboxHolder.classList.remove('is-collapsed')
    $checkboxInput.removeAttribute('hidden')
  }
}

const clearError = (event: Event): void => {
  const target = event.target as HTMLInputElement
  if (target.name === 'promo-popup-email') {
    clearValidationError($emailInput, $emailErrorWrapper)
  } else if (target.name === 'promo-popup-rodo-checkbox') {
    clearValidationError($checkmarkWrapper, $checkboxErrorWrapper)
  }
}

const isFormValid = (): boolean =>
  !emptyValidator($emailInput.value) && !emailValidator($emailInput.value) && !checkedValidator($checkboxLabel.checked)

const sendData = (payload: FormPayload): Promise<void> =>
  promiseWrapper<void, FormPayload>(`${USER_BASE_URL_IK}/api/public/cmsPopupPromoCodeEmail`, 'POST', payload)

const onSubmit = async (event: Event): Promise<void> => {
  event.preventDefault()

  checkEmail($emailInput, $emailErrorWrapper)
  if (!$checkboxHolder.classList.contains('is-collapsed')) {
    checkCheckbox($checkboxLabel, $checkboxErrorWrapper, $checkmarkWrapper)
  }

  if (!isFormValid()) return

  toggleSpinner($submitButton)

  try {
    const payload: FormPayload = {
      email: $emailInput.value,
      language: getCurrentLanguage(),
    }

    await sendData(payload)

    $modalStep1.hidden = true
    $modalStep2.hidden = false
    storageService.setItem('promo_popup_closed', 'true')

    pushDataLayer('form_submitted', { form_name: 'Formularz exit popup' })
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 422) {
      $accountExistsErrorWrapper.hidden = false
    } else {
      $serverErrorWrapper.hidden = false
    }
  } finally {
    toggleSpinner($submitButton)
  }
}

export const initExitModal = (): void => {
  if (!$modal) return

  $modalCloseIcon?.addEventListener('click', closeModal)
  $modalCloseButton?.addEventListener('click', closeModal)
  $form?.addEventListener('submit', onSubmit)

  $checkboxLabel?.addEventListener('change', (e) => {
    clearError(e)
    toggleCheckbox()
  })

  $emailInput?.addEventListener('input', (e) => {
    clearError(e)
    showCheckbox()
  })

  showModal()
}
